import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  createDataTableColumnHelper,
  DataTable,
  Label,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
} from "@beweegbaas/ui";
import { createFileRoute, stripSearchParams } from "@tanstack/react-router";
import { useMemo } from "react";
import { useDateFormatter } from "#/hooks/use-date-formatter";
import { ErrorBoundaryComponent } from "#/components/error-boundary";
import { TruncatedText } from "#/components/truncated-text";
import { getRapportDrawerNotities } from "#/modules/rapporten/rapporten.functions";
import {
  NOTITIES_SEARCH_DEFAULTS,
  notitiesSearchSchema,
} from "#/modules/student/schema.search.notities";
import { renderMultiSelectValue } from "#/modules/util.helpers/utils.select";
import { getNotitieTypeTranslation } from "#/modules/util.i18n/enum-mapping";
import { m } from "#/paraglide/messages";
import type { components } from "#/schema";
import { NotitieType } from "#/schema.nswag";

const NotitieTypeValues = Object.values(NotitieType);

export const Route = createFileRoute(
  "/$locale/{-$schoolyear}/_authenticated/$school/reports/flow/$id/step/3/$class/drawer/notities/$leerlingId",
)({
  validateSearch: notitiesSearchSchema,
  // Keeps default filter/pagination values out of the URL.
  search: { middlewares: [stripSearchParams(NOTITIES_SEARCH_DEFAULTS)] },
  // Narrowed to exactly the keys the loader/server-fn uses — never the whole search object.
  loaderDeps: ({ search }) => ({
    searchTerm: search.searchTerm,
    from: search.from,
    to: search.to,
    afgerondAtFrom: search.afgerondAtFrom,
    afgerondAtTo: search.afgerondAtTo,
    notitieTypes: search.notitieTypes,
    includeFullHistory: search.includeFullHistory,
    page: search.page,
    pageSize: search.pageSize,
  }),
  loader: ({ params, deps }) =>
    getRapportDrawerNotities({
      data: {
        rapportId: params.id,
        leerlingId: params.leerlingId,
        ...deps,
      },
    }),
  component: ReportsDrawerNotitiesPage,
  errorComponent: ReportsDrawerNotitiesError,
});

type DrawerNotities = Awaited<ReturnType<typeof getRapportDrawerNotities>>["notities"];
type NotitieRow = NonNullable<NonNullable<DrawerNotities>["data"]>[number];

const helper = createDataTableColumnHelper<NotitieRow>();

// Two variants: in-range tables highlight rows and use the default badge variant.
function makeColumns(formatter: Intl.DateTimeFormat, highlight: boolean) {
  return helper.columns([
    helper.display({
      id: "notitie",
      header: () => m["common.notitie"](),
      meta: { cellClassName: "wrap py-3" },
      cell: ({ row }) => (
        <>
          <span className="text-muted-foreground mb-1 flex items-center gap-1 text-xs">
            {getNotitieTypeTranslation(row.original.notitieType)}
            {row.original.context && (
              <span className="italic">
                {" - "}
                <TruncatedText
                  text={row.original.context}
                  maxLength={30}
                  tooltipText={row.original.context}
                />
              </span>
            )}
          </span>
          <div className="whitespace-pre-wrap">
            {row.original.notitie || m["rapporten.geen_notitie"]()}
          </div>
        </>
      ),
    }),
    helper.display({
      id: "datum",
      header: () => m["common.fields.date"](),
      meta: { cellClassName: "py-3 text-sm" },
      cell: ({ row }) => (
        <Badge variant={highlight ? "default" : "secondary"}>
          {row.original.afgerondAt
            ? formatter.format(new Date(row.original.afgerondAt))
            : "Onbekend"}
        </Badge>
      ),
    }),
  ]);
}

function ReportsDrawerNotitiesPage() {
  const loaderData = Route.useLoaderData();
  const notities = loaderData.notities || [];
  const formatter = useDateFormatter({
    year: "2-digit",
    month: "short",
    day: "numeric",
  });

  const filters = Route.useSearch();
  const navigate = Route.useNavigate();

  const selectedNotitieTypes = filters.notitieTypes || [];

  const notitieTypeItems = NotitieTypeValues.map((type) => ({
    id: type,
    label: getNotitieTypeTranslation(type),
  }));

  const notitiesInRange = notities.data?.filter((n) => n.withinRange === true) || [];
  const notitiesOutsideRange = notities.data?.filter((n) => n.withinRange !== true) || [];

  const highlightColumns = useMemo(() => makeColumns(formatter, true), [formatter]);
  const defaultColumns = useMemo(() => makeColumns(formatter, false), [formatter]);

  const renderNotitiesTable = (notitiesList: typeof notities.data, highlight = false) => {
    if (!notitiesList || notitiesList.length === 0) {
      return null;
    }

    return (
      <DataTable
        aria-label={m["rapporten.notities.title"]()}
        columns={highlight ? highlightColumns : defaultColumns}
        data={notitiesList}
        getRowClassName={() => (highlight ? "bg-primary/5" : undefined)}
      />
    );
  };

  return (
    <div className="pt-10">
      <div className="mb-4 px-4">
        <h3 className="m-0 mb-4">Notities voor {notities.leerlingNaam}</h3>
        <div className="flex flex-col gap-3">
          <Select
            multiple
            value={selectedNotitieTypes}
            onValueChange={(value) => {
              void navigate({
                search: (prev) => ({ ...prev, notitieTypes: value ?? [] }),
                replace: true,
                resetScroll: false,
              });
            }}
          >
            <SelectTrigger className="w-full" aria-label={m["rapporten.notitie_types"]()}>
              <SelectValue>
                {(value: components["schemas"]["NotitieType"][] | null) =>
                  renderMultiSelectValue(
                    value,
                    notitieTypeItems.map((item) => ({
                      value: item.id,
                      label: item.label,
                    })),
                    "Filter op type",
                  )
                }
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                {notitieTypeItems.map((item) => (
                  <SelectItem key={item.id} value={item.id}>
                    {item.label}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
          <div className="flex items-center gap-2">
            <Switch
              id="includeFullHistory"
              size="sm"
              checked={filters.includeFullHistory}
              onCheckedChange={(checked) =>
                navigate({
                  search: (prev) => ({ ...prev, includeFullHistory: checked }),
                  replace: true,
                  resetScroll: false,
                })
              }
            />
            <Label htmlFor="includeFullHistory" className="cursor-pointer text-sm">
              {m["student.dashboard.notes.includeFullHistory"]()}
            </Label>
          </div>
        </div>
      </div>
      {notities.data.length === 0 ? (
        <p className="px-4 pb-10 text-gray-500">
          Geen notities gevonden voor {notities.leerlingNaam}.
        </p>
      ) : (
        <>
          {notitiesInRange.length > 0 && (
            // -mr-6 cancels the drawer popup's visible right padding so the card runs edge to edge.
            <div className="-mr-6 mb-4">
              <Card className="gap-0 rounded-none bg-blue-50 pt-3 pb-0 shadow">
                <CardHeader className="m-0 px-4">
                  <CardTitle className="text-sm text-blue-700 dark:text-blue-300">
                    In rapport periode
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-0">
                  {renderNotitiesTable(notitiesInRange, true)}
                </CardContent>
              </Card>
            </div>
          )}

          {notitiesOutsideRange.length > 0 && (
            <div className="-mr-6">{renderNotitiesTable(notitiesOutsideRange, false)}</div>
          )}
        </>
      )}
    </div>
  );
}

function ReportsDrawerNotitiesError({ error }: { error: unknown }) {
  const params = Route.useParams();
  return <ErrorBoundaryComponent error={error} params={params} />;
}
